/**
 * Badge Component
 *
 * A reusable badge component for displaying status, labels, and categories.
 * Supports different variants, sizes, and interactive states.
 */

import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the badge
   */
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'outline';

  /**
   * Size of the badge
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Make badge full rounded (pill shape)
   */
  rounded?: boolean;
}

// =============================================================================
// STYLES
// =============================================================================

/**
 * Get the CSS classes for badge variants
 */
const getVariantClasses = (variant: BadgeProps['variant']) => {
  switch (variant) {
    case 'default':
      return 'bg-[var(--surface-hover)] text-[var(--text-heading)] border-[var(--border-default)]';
    case 'primary':
      return 'bg-primary-100 text-primary-800 border-primary-200';
    case 'secondary':
      return 'bg-[var(--surface-hover)] text-[var(--text-secondary)] border-[var(--border-default)]';
    case 'success':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'error':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'info':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'outline':
      return 'bg-transparent text-[var(--text-body)] border-[var(--border-default)]';
    default:
      return 'bg-[var(--surface-hover)] text-[var(--text-heading)] border-[var(--border-default)]';
  }
};

/**
 * Get the CSS classes for badge sizes
 */
const getSizeClasses = (size: BadgeProps['size']) => {
  switch (size) {
    case 'sm':
      return 'px-2 py-0.5 text-xs';
    case 'md':
      return 'px-2.5 py-1 text-xs';
    case 'lg':
      return 'px-3 py-1.5 text-sm';
    default:
      return 'px-2.5 py-1 text-xs';
  }
};

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Badge component for status and labels
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      rounded = true,
      children,
      ...props
    },
    ref
  ) => {
    const badgeClassName = cn(
      // Base styles
      [
        'inline-flex items-center justify-center',
        'font-medium',
        'border',
        'transition-colors duration-200',
      ].join(' '),

      // Variant styles
      getVariantClasses(variant),

      // Size styles
      getSizeClasses(size),

      // Rounded
      rounded ? 'rounded-full' : 'rounded-md',

      // Custom className
      className
    );

    return (
      <div ref={ref} className={badgeClassName} {...props}>
        {children}
      </div>
    );
  }
);

Badge.displayName = 'Badge';

// =============================================================================
// EXPORTS
// =============================================================================

export default Badge;
